import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { NumberDisplay } from './NumberDisplay';

/**
 * Modal component for displaying adjustment reason codes and quantities
 *
 * @param {boolean} isOpen - Whether the modal is open
 * @param {function} onClose - Function to call when the modal is closed
 * @param {string} itemId - SKU or Lot ID
 * @param {function} fetchAdjustmentReasons - Function to fetch adjustment reasons data
 * @param {string} companySourceId - Company source ID
 * @param {string} title - Modal title
 * @param {string} groupBy - The current grouping mode ('sku' or 'lot')
 * @param {string} adjustmentType - Filter results by 'positive' or 'negative' adjustments
 * @param {number} packedOutUnits - Number of units packed out (for negative adjustment modal)
 * @param {object} options - Additional configuration options
 * @param {string} options.className - Additional CSS classes for the modal
 */
function AdjustmentReasonModal({
    adjustmentType,
    companySourceId,
    fetchAdjustmentReasons,
    groupBy = 'sku',
    isOpen,
    itemId,
    onClose,
    options = {},
    packedOutUnits = 0,
    title = 'Adjustment Reasons',
}) {
    const { className = '' } = options;

    const [adjustmentReasons, setAdjustmentReasons] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const modalRef = useRef(null);

    // Fetch data when modal opens
    useEffect(() => {
        const fetchData = async () => {
            if (!isOpen || !itemId || !fetchAdjustmentReasons) {
                return;
            }

            setIsLoading(true);
            setError(null);

            const result = await fetchAdjustmentReasons(itemId, companySourceId, groupBy);

            if (!result.status) {
                setError(result.error || 'Failed to fetch adjustment reasons');
                setAdjustmentReasons([]);
            } else if (result.data && result.data.rows) {
                const { rows } = result.data;

                let reasonsArray = [];

                if (rows.positive) {
                    const positiveReasons = Object.entries(rows.positive).map(([reason, value]) => ({
                        code: reason,
                        quantity: value,
                        type: 'positive',
                    }));
                    reasonsArray = [...reasonsArray, ...positiveReasons];
                }

                if (rows.negative) {
                    const negativeReasons = Object.entries(rows.negative).map(([reason, value]) => ({
                        code: reason,
                        quantity: value,
                        type: 'negative',
                    }));
                    reasonsArray = [...reasonsArray, ...negativeReasons];
                }

                setAdjustmentReasons(reasonsArray);
            } else {
                setAdjustmentReasons([]);
            }

            setIsLoading(false);
        };

        fetchData();
    }, [isOpen, itemId, fetchAdjustmentReasons, groupBy, companySourceId]);

    // Close modal when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (modalRef.current && !modalRef.current.contains(event.target)) {
                onClose();
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen, onClose]);

    // Close modal when pressing Escape key
    useEffect(() => {
        const handleKeyDown = (event) => {
            if (event.key === 'Escape') {
                onClose();
            }
        };

        if (isOpen) {
            document.addEventListener('keydown', handleKeyDown);
        }

        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [isOpen, onClose]);

    // Don't render if not open
    if (!isOpen) return null;

    // Render loading indicator
    const renderLoading = () => (
        <div className="flex justify-center items-center py-8">
            <div className="spinner" />
        </div>
    );

    // Render error message
    const renderError = () => <div className="text-center text-red-500 py-4">{error}</div>;

    // Render table content
    const renderContent = () => {
        if (isLoading) return renderLoading();

        // Even if there's an error, proceed with empty reasons array
        // instead of displaying an error message

        // Filter reasons based on adjustmentType if provided
        const filteredReasons = adjustmentType
            ? adjustmentReasons.filter((reason) => reason.type === adjustmentType)
            : adjustmentReasons;

        // For negative adjustments, prepend the Packed Out units (only if > 0)
        let displayReasons = filteredReasons;
        if (adjustmentType === 'negative' && packedOutUnits !== 0) {
            displayReasons = [{ code: 'Packed Out', quantity: packedOutUnits, type: 'negative' }, ...filteredReasons];
        }

        // Special case: If there are no reasons at all but we have packedOutUnits,
        // we should still show the Packed Out row
        // if (displayReasons.length === 0 && adjustmentType === 'negative' && packedOutUnits !== 0) {
        //     displayReasons = [{ code: 'Packed Out', quantity: packedOutUnits, type: 'negative' }];
        // }

        return displayReasons.length > 0 ? (
            <table className="w-full">
                <tbody className="text-gray-700">
                    {displayReasons.map((reason, index) => (
                        <tr key={index} className={index % 2 === 0 ? 'bg-gray-50' : ''}>
                            <td className="py-1.5 pr-4 text-xs">{reason.code}</td>
                            <td className="py-1.5 text-right">
                                <NumberDisplay
                                    value={reason.quantity}
                                    isChangeValue
                                    className="inline-block px-1 font-bold text-xs"
                                />
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        ) : (
            <p className="text-center text-gray-500 py-3">No adjustment reasons found</p>
        );
    };

    return createPortal(
        <div className="fixed inset-0 flex items-center justify-center z-50 inventory-history-v2">
            <div
                ref={modalRef}
                className={`bg-white rounded-md shadow-lg border border-gray-200 p-0 max-w-md w-full ${className}`}
            >
                {/* Modal header */}
                <div className="flex justify-between items-center p-3 modal-header rounded-t-md">
                    <h3 className="text-sm font-medium text-gray-700">{title}</h3>
                    <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-500">
                        <span className="sr-only">Close</span>
                        <svg
                            className="h-4 w-4"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M6 18L18 6M6 6l12 12"
                            />
                        </svg>
                    </button>
                </div>

                {/* Modal content */}
                <div className="max-h-80 overflow-y-auto py-2 px-3 bg-white">{renderContent()}</div>
            </div>
        </div>,
        document.body,
    );
}

export { AdjustmentReasonModal };
